/**
 * 计费套餐 Hook
 * 从 API 动态获取套餐数据
 */

import { useState, useEffect, useCallback, useRef } from "react";

// 套餐基础类型
export interface BillingPlan {
  id: string;
  code: string;
  family: "personal" | "addon" | "tenant" | "one_time";
  tier?: string;
  billingCycle?: string;
  kind?: string;
  currency: string;
  price?: number;
  baseSeats?: number;
  basePrice?: number;
  extraSeatPrice?: number;
  displayOrder: number;
  active: boolean;
  featured: boolean;
  metadata?: any;
}

// 租户套餐详细类型
export interface TenantPlanDetails extends BillingPlan {
  seats: {
    min: number;
    max: number;
    included: number;
    extraPrice: number;
    extraPriceYearly: number;
  };
  quotaAllocation: {
    mode: "SHARED" | "PER_SEAT";
    multiplier: number;
    perSeatQuota?: number;
  };
  management: {
    maxAdmins: number;
    canInvite: boolean;
    canRemoveMembers: boolean;
    canAssignQuota: boolean;
    canViewReports: boolean;
    canExportData: boolean;
  };
  featuresConfig: {
    api: boolean;
    apiRateLimit?: number;
    sso: boolean;
    whiteLabel: boolean;
    customBranding: boolean;
    advancedReports: boolean;
    biIntegration: boolean;
    dataExport: string[];
    customCourses: boolean;
  };
  trial: {
    enabled: boolean;
    days: number;
    autoConvert: boolean;
    requirePaymentMethod: boolean;
    fallbackPlan?: string;
    /** 自动批准试用时的席位上限，null 表示不限制 */
    maxSeats: number | null;
  };
  support: {
    level: string;
    responseHours: number;
    hasCSM: boolean;
    slaTier?: string;
  };
  eligibility?: {
    requiresVerification: boolean;
    verificationType: string;
    discountPercent: number;
  };
  contract?: {
    type: string;
    minTermMonths: number;
    autoRenewal: boolean;
    customPricing: boolean;
  };
}

// 价格计算结果
export interface PriceBreakdown {
  basePrice: number;
  extraSeatsPrice: number;
  discount: number;
  subtotal: number;
  tax: number;
  total: number;
  currency: string;
  billingCycle: string;
  seats: number;
  includedSeats: number;
  extraSeats: number;
  pricePerExtraSeat: number;
  savings?: number;
}

// 分组套餐
export interface GroupedPlans {
  personal: BillingPlan[];
  oneTime: BillingPlan[];
  addon: BillingPlan[];
  tenant: TenantPlanDetails[];
}

interface UseBillingPlansOptions {
  family?: string;
  tier?: string;
  includeInactive?: boolean;
}

interface UseBillingPlansReturn {
  plans: BillingPlan[];
  grouped: GroupedPlans | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * 获取所有套餐
 */
export function useBillingPlans(
  options: UseBillingPlansOptions = {},
): UseBillingPlansReturn {
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [grouped, setGrouped] = useState<GroupedPlans | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (options.family) params.set("family", options.family);
      if (options.tier) params.set("tier", options.tier);
      if (options.includeInactive) params.set("includeInactive", "true");

      const url = `/api/billing/plans${params.toString() ? `?${params}` : ""}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error("Failed to fetch plans");
      }

      const data = await response.json();
      setPlans(data.plans || []);
      setGrouped(data.grouped || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [options.family, options.tier, options.includeInactive]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  return { plans, grouped, loading, error, refetch: fetchPlans };
}

/**
 * 获取单个套餐详情
 */
export function usePlanDetails(code: string | null) {
  const [plan, setPlan] = useState<TenantPlanDetails | BillingPlan | null>(
    null,
  );
  const [loading, setLoading] = useState(!!code);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) {
      setPlan(null);
      return;
    }

    const fetchPlan = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`/api/billing/plans/${code}`);
        if (!response.ok) {
          throw new Error("Failed to fetch plan");
        }

        const data = await response.json();
        setPlan(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    };

    fetchPlan();
  }, [code]);

  return { plan, loading, error };
}

/**
 * 计算价格
 */
export function useCalculatePrice() {
  const [breakdown, setBreakdown] = useState<PriceBreakdown | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef<number>(0);

  const calculate = useCallback(
    async (
      planCode: string,
      seats?: number,
      billingCycle?: "monthly" | "yearly",
      couponCode?: string,
    ) => {
      // 取消之前的请求
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // 创建新的 AbortController
      const controller = new AbortController();
      abortControllerRef.current = controller;

      // 生成请求 ID
      const currentRequestId = ++requestIdRef.current;

      try {
        setLoading(true);
        setError(null);

        const response = await fetch("/api/billing/plans/calculate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            planCode,
            seats,
            billingCycle,
            couponCode,
          }),
          signal: controller.signal,
        });

        // 检查是否是最新的请求
        if (currentRequestId !== requestIdRef.current) {
          return null;
        }

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || "Failed to calculate price");
        }

        const data = await response.json();
        setBreakdown(data.breakdown);
        return data.breakdown;
      } catch (err) {
        // 忽略被取消的请求
        if (err instanceof Error && err.name === "AbortError") {
          return null;
        }

        const message = err instanceof Error ? err.message : "Unknown error";
        setError(message);
        return null;
      } finally {
        // 只在当前请求未被取消时设置 loading
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setBreakdown(null);
    setError(null);
  }, []);

  return { breakdown, loading, error, calculate, reset };
}

/**
 * 格式化价格
 */
export function formatPrice(amount: number, currency: string = "USD"): string {
  const symbols: Record<string, string> = {
    USD: "$",
    CNY: "¥",
    EUR: "€",
    JPY: "¥",
    KRW: "₩",
  };
  const symbol = symbols[currency] || currency;
  return `${symbol}${amount.toLocaleString()}`;
}

/**
 * 获取套餐显示名称
 */
export function getPlanDisplayName(plan: BillingPlan): string {
  const tierNames: Record<string, string> = {
    free: "Free",
    basic: "Basic",
    pro: "Pro",
    team: "Team",
    business: "Business",
    education: "Education",
    startup: "Startup",
    enterprise: "Enterprise",
  };
  return tierNames[plan.tier?.toLowerCase() || ""] || plan.tier || plan.code;
}

/**
 * 获取计费周期显示名称
 */
export function getBillingCycleLabel(cycle: string | undefined): string {
  const labels: Record<string, string> = {
    monthly: "/月",
    yearly: "/年",
    one_time: "一次性",
  };
  return labels[cycle || ""] || "";
}
